import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface Dto { id: number; branchTypeCode: string; description: string; deleted: boolean; }
const schema = z.object({ branchTypeCode: z.string().min(1).max(20), description: z.string().min(1).max(100) });
type Form = z.infer<typeof schema>;

export function BranchTypesPage() {
  const [editing, setEditing] = useState<Dto | null>(null);
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["branch-types"], queryFn: () => apiFetch<Dto[]>("/api/branch-types") });
  const upsert = useMutation({
    mutationFn: (v: Form) => editing
      ? apiFetch<Dto>(`/api/branch-types/${editing.id}`, { method: "PATCH", body: JSON.stringify(v) })
      : apiFetch<Dto>("/api/branch-types", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["branch-types"] }); setEditing(null); setCreating(false); },
  });

  const cols: DataGridColumn<Dto>[] = [
    { id: "code", header: "Code", accessor: (r) => <button className="underline" onClick={() => setEditing(r)}>{r.branchTypeCode}</button> },
    { id: "desc", header: "Description", accessor: (r) => r.description },
  ];

  return (
    <ListPage title="Branch types" primaryAction={<IntentButton onClick={() => setCreating(true)}>New branch type</IntentButton>}>
      <DataGrid rows={data ?? []} columns={cols} rowKey={(r) => r.id}
        totalRows={data?.length ?? 0} pageIndex={0} pageSize={200} onPageChange={() => { /* fixed page */ }} />
      <DetailsDrawer open={creating || editing !== null}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
        title={editing ? "Edit branch type" : "New branch type"}>
        <EntityForm schema={schema} defaultValues={editing ?? { branchTypeCode: "", description: "" }} onSubmit={(v) => upsert.mutate(v)}>
          {(f) => (
            <div className="space-y-3">
              <div><Label htmlFor="bc">Code</Label><Input id="bc" {...f.register("branchTypeCode")} /></div>
              <div><Label htmlFor="bd">Description</Label><Input id="bd" {...f.register("description")} /></div>
              <Button type="submit" disabled={upsert.isPending}>{editing ? "Save" : "Create"}</Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
